import { Component, Input, OnDestroy } from '@angular/core';
import { Option } from '@vendasta/forms';
import { EMPTY, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { SwitchingWorkState, WorkStates } from '@vendasta/rx-utils/work-state';
import { SubscriptionList } from '@vendasta/rx-utils';
import { openSecureNewTab } from '../../../secure';
import { SocialURLs } from '../../../interface';
import { BusinessInfo, OpportunityBusinessService } from '../../business-service/opportunity-business.service';

export interface MarketNameInput {
  partnerId: string;
  marketId: string;
}

@Component({
  selector: 'sales-ui-business-info',
  templateUrl: './business-info.component.html',
  styleUrls: ['./business-info.component.scss'],
})
export class BusinessInfoComponent implements OnDestroy {
  protected vCategoryIds$: Observable<string[]>;
  protected socialUrls$: Observable<SocialURLs>;
  protected marketName$: Observable<string>;

  private readonly marketNameState$ = new SwitchingWorkState<MarketNameInput, string>((input: MarketNameInput) => {
    const partnerId = input?.partnerId;
    const marketId = input?.marketId;

    if (!partnerId || !marketId) {
      return EMPTY;
    }
    return this.businessService.getMarketName(partnerId, marketId).pipe(take(1));
  });
  readonly marketNameState: WorkStates<string> = this.marketNameState$;
  readonly marketNameLoading$ = this.marketNameState.isLoading$;
  readonly marketNameFailed$ = this.marketNameState.isSuccess$.pipe(map((isSuccess) => !isSuccess));

  @Input() tags: Option[];
  @Input() pipelineBoardSlideoutStyling = false;
  @Input() set businessId(id: string) {
    this.businessService.loadBusinessAndSetSelected(id);
  }

  public businessData$: Observable<BusinessInfo>;
  protected readonly businessDetailsUrl$: Observable<string>;
  protected readonly businessEditUrl$: Observable<string>;
  private readonly subscriptions = SubscriptionList.new();

  constructor(private readonly businessService: OpportunityBusinessService) {
    this.businessData$ = this.businessService.currentBusiness$;
    this.subscriptions.add((this.marketName$ = this.marketNameState.workResults$));
    this.triggerGetMarketNameWork();
    this.businessDetailsUrl$ = this.businessData$.pipe(
      switchMap((business) => this.businessService.getBusinessDataUrl$(business?.businessId, false)),
    );
    this.businessEditUrl$ = this.businessData$.pipe(
      switchMap((business) => this.businessService.getBusinessDataUrl$(business?.businessId, true)),
    );
    this.socialUrls$ = this.businessData$.pipe(map((business) => business.socialUrls));
    this.vCategoryIds$ = this.businessData$.pipe(
      distinctUntilChanged((x, y) => x.vCategories === y.vCategories),
      map((business) => business.vCategories),
    );
  }

  triggerGetMarketNameWork(): void {
    firstValueFrom(this.businessData$).then((bd) => {
      this.marketNameState$.startWork({
        partnerId: bd?.partnerId,
        marketId: bd?.marketId,
      });
    });
  }

  openUrl(url: string): void {
    openSecureNewTab(url);
  }

  googleSearchForBusiness(): void {
    this.subscriptions.add(
      this.businessData$.pipe(
        take(1),
        map((business) => {
          const city = business.city ?? '';
          const state = business.state ?? '';
          const googleSearchPhrase = `${business.name} ${city} ${state}`.replace(/\s+/g, ' ').trim();
          openSecureNewTab(`http://www.google.com/search?q=${googleSearchPhrase}`);
        }),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }
}
